import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import styles from "./modal.module.css";

/** Every mounted dialog, in the order it opened. Escape belongs to the last. */
const open: object[] = [];

/**
 * Every dialog in the product, dismissed the same way.
 *
 * ── Why this exists (client's manual QA, items 23 and 30) ───────────────────
 *
 * Eight dialogs were hand-rolled, each a `div` with `role="dialog"` and a
 * backdrop that did nothing when you clicked it. The client reported the same
 * defect twice, once for the calendar and once for the patient file — *"any
 * popup in the application should be closable by clicking out of it"* — which
 * is the shape of a missing primitive rather than of two bugs.
 *
 * So there is one now. A dialog that opts out of this is opting out visibly,
 * rather than by having been written before anybody thought about it.
 *
 * ── Two dismissals, and a third that is deliberately absent ─────────────────
 *
 * **The backdrop**, and **Escape**. Both mean the same thing: *I did not want
 * this*. Nothing here saves on the way out — a dialog with unsaved work is the
 * caller's problem, and the caller is the only thing that knows whether there
 * is any.
 *
 * There is no close-on-blur. A dialog that vanished when the window lost focus
 * would take a half-typed patient name with it.
 *
 * ── The bug this is written to avoid ────────────────────────────────────────
 *
 * **`mousedown`, and only when it starts *and* ends on the backdrop.** The
 * obvious implementation listens for a click on the backdrop and checks
 * `event.target === event.currentTarget` — which closes the dialog when a
 * selection drag begins inside it and releases outside, because the browser
 * reports that click against the common ancestor. Selecting a patient's phone
 * number and releasing past the edge of the panel would throw the form away.
 *
 * So the press is recorded, and the release only counts if the press was on the
 * backdrop too.
 *
 * ── Escape closes the top dialog, not every dialog ──────────────────────────
 *
 * A dialog can open another — New patient asks "that number is already on a
 * file" over itself. Each listens for Escape on `window`, so both used to close
 * at once, and dismissing the question threw away the patient typed under it.
 */
export function Modal({
  label,
  onClose,
  children,
  testId,
  panelClassName,
  backdropClassName,
}: {
  /** Names the dialog for a screen reader. Not drawn. */
  label: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
  /**
   * **Replaces** the default panel class rather than adding to it.
   *
   * Most dialogs are a 520px form and want the default. An image viewer and a
   * long list are not, and they already had their own panel styling before this
   * primitive existed — appending would leave two width and padding
   * declarations of equal specificity fighting over bundle order, which is a
   * bug that appears when an unrelated import moves.
   */
  panelClassName?: string;
  /** **Added to** the backdrop, never replacing it — a dimmer or blurred scrim. */
  backdropClassName?: string;
}): React.JSX.Element {
  const pressedBackdrop = useRef(false);
  const panel = useRef<HTMLDivElement | null>(null);

  // Read at the keypress, so a caller passing a fresh arrow each render does not
  // re-register — which would move this dialog to the top of `open`.
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });

  useEffect(() => {
    const self = {};
    open.push(self);

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && open[open.length - 1] === self) close.current();
    };
    // On `window` rather than the panel, so Escape works before anything inside
    // has been focused — which is the state a dialog opens in.
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      open.splice(open.indexOf(self), 1);
    };
  }, []);

  useEffect(() => {
    // Focus lands in the dialog so Tab stays here and Escape has a subject.
    panel.current?.focus();
  }, []);

  /**
   * **Rendered into `document.body`, not where it is declared** (2026-10-16).
   * A dialog opened from inside the appointment panel — Complete visit — sat in
   * the panel's stacking context, so the day grid's chair headers (z-index 6)
   * drew on top of its backdrop. A portal puts every dialog above the page
   * whatever it is opened from; React events still bubble through the tree.
   */
  return createPortal(
    <div
      className={
        backdropClassName === undefined
          ? styles.backdrop
          : `${styles.backdrop ?? ""} ${backdropClassName}`
      }
      data-testid={testId}
      onMouseDown={(event) => {
        pressedBackdrop.current = event.target === event.currentTarget;
      }}
      onMouseUp={(event) => {
        if (pressedBackdrop.current && event.target === event.currentTarget) onClose();
        pressedBackdrop.current = false;
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={`${styles.shell} ${panelClassName ?? styles.panel}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
